import { RealEstateDto, AddressDto } from 'api/real-estate';
import { GenericLoading } from 'components/ui/Loader';
import { NsKey } from 'enums/NsKey';
import { Sizes } from 'enums/sizes';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { useDialog } from 'hooks/useDialog';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { usePanel } from 'hooks/usePanel';
import { LightRealEstateResult } from 'features/subscription-process/home-insurance/components/real-estate/RealEstateSearchBar';
import { Button } from 'components/ui/Button';
import { Home, Plus, Minus } from 'lucide-react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { borrowersAPI } from 'services/swagger-api';
import { realEstateAPI } from 'services/swagger-api/realEstateAPI';
import {
  CreateBorrowerRealEstateCommand,
  UpdateBorrowerRealEstateCommand
} from 'api/borrower';
import { PropertyTypeEnum } from 'api/real-estate';
import { BorrowerBienSearchBar } from '../../../components/borrowers/real-estate/BorrowerBienSearchBar';
import {
  BorrowerRealEstateFormValues,
  borrowerRealEstateValidationSchema,
  emptyBorrowerRealEstateForm
} from './borrowerRealEstateForm';
import { BorrowerRealEstateFormLayout } from './BorrowerRealEstateFormLayout';
import { BorrowerRealEstateDetailsSection } from './BorrowerRealEstateDetailsSection';
import { BorrowerRealEstateSummary } from './BorrowerRealEstateSummary';
import { BorrowerRealEstateEditDialog } from './BorrowerRealEstateEditDialog';
import { useBorrowerBiens } from './useBorrowerBiens';

type PanelView = 'loading' | 'create' | 'summary';

export interface InsuredAddress {
  line1?: string | null;
  line2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
}

interface BorrowerRealEstatePanelProps {
  personneId: string;
  borrowerProjectId: string;
  currentBienId?: string;
  insuredAddress?: InsuredAddress;
  onRefresh: () => void;
  onClose: () => void;
}

const mapFormToAddress = (values: BorrowerRealEstateFormValues) => ({
  line1: values.address.address,
  line2: values.line2 || undefined,
  zipCode: values.address.postalCode,
  city: values.address.city,
  country: values.address.country
});

const normalize = (value?: string | null) => (value ?? '').trim().toLowerCase();

const isExactAddressMatch = (bienAddress: AddressDto, insured: InsuredAddress) =>
  Boolean(insured.line1) &&
  Boolean(insured.postalCode) &&
  Boolean(insured.city) &&
  normalize(bienAddress.line1) === normalize(insured.line1) &&
  normalize(bienAddress.zipCode) === normalize(insured.postalCode) &&
  normalize(bienAddress.city) === normalize(insured.city);

const buildInitialFormValues = (
  insured: InsuredAddress | undefined
): BorrowerRealEstateFormValues => {
  if (!insured || !insured.line1 || !insured.postalCode || !insured.city) {
    return emptyBorrowerRealEstateForm;
  }
  const country = insured.country || 'FR';
  return {
    ...emptyBorrowerRealEstateForm,
    addressSearch: `${insured.line1}, ${insured.postalCode} ${insured.city}`,
    line2: insured.line2 ?? '',
    address: {
      address: insured.line1,
      postalCode: insured.postalCode,
      city: insured.city,
      country
    }
  };
};

const formatBienAddress = (bien: RealEstateDto): string =>
  [bien.address?.line1, bien.address?.zipCode, bien.address?.city].filter(Boolean).join(', ');

const toLightRealEstateResult = (bien: RealEstateDto): LightRealEstateResult => ({
  id: bien.id!,
  name: bien.name || `Bien ${bien.id!.substring(0, 8)}`,
  address: bien.address,
  area: bien.area,
  roomCount: bien.roomCount,
  charges: bien.charges,
  ownersCount: bien.owners?.length || 0,
  data: bien
});

export const BorrowerRealEstatePanel: FC<BorrowerRealEstatePanelProps> = ({
  personneId,
  borrowerProjectId,
  currentBienId,
  insuredAddress,
  onRefresh,
  onClose
}) => {
  const { t } = useDynamicTranslation([NsKey.Contractualization, NsKey.RealEstate, NsKey.Common]);
  const { setPanelActions } = usePanel();
  const { openDialog } = useDialog();

  const [view, setView] = useState<PanelView>(currentBienId ? 'loading' : 'create');
  const [currentBien, setCurrentBien] = useState<RealEstateDto | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { biens, isLoading: biensLoading, hasBiens } = useBorrowerBiens(personneId);

  // On ne pré-remplit le formulaire avec l'adresse de l'assuré que si aucun bien existant
  // ne correspond déjà à cette adresse (sinon on propose de réutiliser ce bien, cf. encart).
  const insuredAddressMatchesExisting = useMemo(
    () =>
      insuredAddress
        ? biens.some((b) => b.address && isExactAddressMatch(b.address, insuredAddress))
        : false,
    [biens, insuredAddress]
  );

  const initialFormValues = useMemo(
    () => (insuredAddressMatchesExisting ? emptyBorrowerRealEstateForm : buildInitialFormValues(insuredAddress)),
    [insuredAddressMatchesExisting, insuredAddress]
  );

  const fetchBienById = useCallback(async (bienId: string) => {
    try {
      const response = await realEstateAPI.getRealEstateById({ bienId });
      const bien =
        (response as unknown as { realEstate?: RealEstateDto }).realEstate ??
        (response as unknown as RealEstateDto);
      if (bien && bien.id) return bien;
    } catch (error) {
      console.error('Erreur lors du chargement du bien:', error);
    }
    return null;
  }, []);

  // Load current bien
  useEffect(() => {
    if (!currentBienId) {
      setCurrentBien(null);
      setView('create');
      return;
    }
    let cancelled = false;
    setView('loading');
    fetchBienById(currentBienId).then((bien) => {
      if (cancelled) return;
      if (bien) {
        setCurrentBien(bien);
        setView('summary');
      } else {
        setView('create');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [currentBienId, fetchBienById]);

  // Clear panel actions when NOT in create view
  useEffect(() => {
    if (view !== 'create') {
      setPanelActions(undefined);
    }
  }, [view, setPanelActions]);

  // ── Link existing ──

  const handleSelectExisting = useCallback(
    async (property: LightRealEstateResult) => {
      try {
        setIsSubmitting(true);
        const response = await borrowersAPI.linkRealEstateToBorrowerProject({
          borrowerProjectId,
          realEstateId: property.id
        });
        if (response.isSuccess) {
          toast.success(t('CONTRACTUALIZATION.REAL_ESTATE.LINK_SUCCESS'));
          onRefresh();
          const refreshed = (await fetchBienById(property.id)) ?? property.data;
          setCurrentBien(refreshed);
          setView('summary');
        } else {
          toast.error(response.errorMessage || t('COMMON.ERROR'));
        }
      } catch (error) {
        console.error(error);
        toast.error(t('COMMON.ERROR'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [borrowerProjectId, onRefresh, t, fetchBienById]
  );

  // ── Create new ──

  const handleCreate = useCallback(
    async (
      values: BorrowerRealEstateFormValues,
      helpers: FormikHelpers<BorrowerRealEstateFormValues>
    ) => {
      try {
        setIsSubmitting(true);
        const command: CreateBorrowerRealEstateCommand = {
          borrowerProjectId,
          area: values.area !== '' ? Number(values.area) : undefined,
          roomCount: values.roomCount !== '' ? Number(values.roomCount) : undefined,
          propertyType: values.propertyType !== '' ? (values.propertyType as PropertyTypeEnum) : undefined,
          address: mapFormToAddress(values)
        };
        const response = await borrowersAPI.createBorrowerRealEstate({
          createBorrowerRealEstateCommand: command
        });
        if (response.isSuccess) {
          toast.success(t('CONTRACTUALIZATION.REAL_ESTATE.CREATE_SUCCESS'));
          onRefresh();
          if (response.id) {
            const bien = await fetchBienById(response.id);
            if (bien) {
              setCurrentBien(bien);
              setView('summary');
              return;
            }
          }
          onClose();
        } else {
          toast.error(response.errorMessage || t('COMMON.ERROR'));
        }
      } catch (error) {
        console.error(error);
        toast.error(t('COMMON.ERROR'));
      } finally {
        setIsSubmitting(false);
        helpers.setSubmitting(false);
      }
    },
    [borrowerProjectId, onRefresh, onClose, t, fetchBienById]
  );

  // ── Edit (dialog) ──

  const handleEditCurrent = useCallback(() => {
    if (!currentBien) return;
    openDialog(
      {
        title: t('CONTRACTUALIZATION.REAL_ESTATE.EDIT_TITLE'),
        size: Sizes['2xl']
      },
      <BorrowerRealEstateEditDialog
        borrowerProjectId={borrowerProjectId}
        existingBien={currentBien}
        onSuccess={async () => {
          onRefresh();
          if (currentBien.id) {
            const refreshed = await fetchBienById(currentBien.id);
            if (refreshed) {
              setCurrentBien(refreshed);
              setView('summary');
            }
          }
        }}
      />
    );
  }, [openDialog, t, borrowerProjectId, currentBien, onRefresh, fetchBienById]);

  const handleChangeBien = useCallback(() => {
    setView('create');
  }, []);

  // ── Render ──

  if (view === 'loading') {
    return (
      <div className="flex items-center justify-center p-10">
        <GenericLoading loading />
      </div>
    );
  }

  if (view === 'summary' && currentBien) {
    return (
      <BorrowerRealEstateSummary
        bien={currentBien}
        onEdit={handleEditCurrent}
        onChange={handleChangeBien}
      />
    );
  }

  // ── Create view (inline in panel) ──
  // On attend le chargement des biens avant de construire le formulaire, pour décider
  // du pré-remplissage sans flash (pré-rempli → vidé).
  if (biensLoading) {
    return (
      <div className="flex items-center justify-center p-10">
        <GenericLoading loading />
      </div>
    );
  }

  return (
    <div className="relative">
      {isSubmitting && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded-lg">
          <GenericLoading loading />
        </div>
      )}
      <Formik<BorrowerRealEstateFormValues>
        enableReinitialize
        validateOnMount
        initialValues={initialFormValues}
        validationSchema={borrowerRealEstateValidationSchema}
        onSubmit={handleCreate}
      >
        {({ isValid, submitForm }) => (
          <BorrowerRealEstateCreateView
            isValid={isValid}
            isSubmitting={isSubmitting}
            biens={biens}
            hasBiens={hasBiens}
            currentBienId={currentBien?.id}
            insuredAddress={insuredAddress}
            showDetails={showDetails}
            setShowDetails={setShowDetails}
            submitForm={submitForm}
            setPanelActions={setPanelActions}
            onSelectExisting={handleSelectExisting}
          />
        )}
      </Formik>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

interface BorrowerRealEstateCreateViewProps {
  isValid: boolean;
  isSubmitting: boolean;
  biens: RealEstateDto[];
  hasBiens: boolean;
  currentBienId?: string;
  insuredAddress?: InsuredAddress;
  showDetails: boolean;
  setShowDetails: (v: boolean) => void;
  submitForm: () => Promise<void> | void;
  setPanelActions: ReturnType<typeof usePanel>['setPanelActions'];
  onSelectExisting: (property: LightRealEstateResult) => void;
}

const BorrowerRealEstateCreateView: FC<BorrowerRealEstateCreateViewProps> = ({
  isValid,
  isSubmitting,
  biens,
  hasBiens,
  currentBienId,
  insuredAddress,
  showDetails,
  setShowDetails,
  submitForm,
  setPanelActions,
  onSelectExisting
}) => {
  const { t } = useDynamicTranslation([NsKey.Contractualization, NsKey.RealEstate, NsKey.Common]);
  const { values } = useFormikContext<BorrowerRealEstateFormValues>();

  // Adresse à comparer : l'adresse saisie si renseignée, sinon celle de l'assuré (cas formulaire
  // non pré-rempli car un bien correspond déjà). Un doublon est interdit : on propose de réutiliser
  // le bien et on bloque la création.
  const matchingBien = useMemo(() => {
    const formAddress: InsuredAddress | undefined =
      values.address.address && values.address.postalCode && values.address.city
        ? { line1: values.address.address, postalCode: values.address.postalCode, city: values.address.city }
        : insuredAddress;
    if (!formAddress?.line1 || !formAddress.postalCode || !formAddress.city) return null;
    return biens.find((b) => b.id && b.address && isExactAddressMatch(b.address, formAddress)) ?? null;
  }, [biens, values.address.address, values.address.postalCode, values.address.city, insuredAddress]);

  useEffect(() => {
    setPanelActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.CREATE',
      submitDisabled: !isValid || isSubmitting || !!matchingBien,
      onSubmit: () => {
        void submitForm();
      }
    });
    return () => setPanelActions(undefined);
  }, [setPanelActions, isValid, isSubmitting, submitForm, matchingBien]);

  return (
    <Form>
      <div className="flex flex-col gap-5 p-4">
        {/* Bien déjà existant à l'adresse saisie : création bloquée, réutilisation proposée */}
        {matchingBien && (
          <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-primary/10">
              <Home className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900">
                {t('CONTRACTUALIZATION.REAL_ESTATE.DUPLICATE_TITLE')}
              </p>
              <p className="truncate text-xs text-gray-600">{formatBienAddress(matchingBien)}</p>
            </div>
            <Button
              type="button"
              color="primary"
              className="flex-shrink-0"
              onClick={() => onSelectExisting(toLightRealEstateResult(matchingBien))}
            >
              {t('CONTRACTUALIZATION.REAL_ESTATE.SUGGESTION_USE')}
            </Button>
          </div>
        )}

        {/* Section 1 : Recherche (visible uniquement si des biens existent) */}
        {hasBiens && (
          <>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {t('CONTRACTUALIZATION.REAL_ESTATE.SEARCH_TITLE')}
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                {t('CONTRACTUALIZATION.REAL_ESTATE.SEARCH_HELP')}
              </p>
            </div>

            <BorrowerBienSearchBar
              biens={biens}
              isLoading={false}
              excludeBienId={currentBienId}
              onSelect={onSelectExisting}
            />

            {/* Séparateur */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-2 text-gray-500 uppercase tracking-wider">
                  {t('COMMON.OR')}
                </span>
              </div>
            </div>
          </>
        )}

        {/* Section 2 : Formulaire adresse */}
        <BorrowerRealEstateFormLayout />

        {/* Section 3 : Caractéristiques (progressive disclosure) */}
        {!showDetails ? (
          <button
            type="button"
            onClick={() => setShowDetails(true)}
            className="inline-flex items-center gap-1.5 self-start text-sm text-primary hover:text-primary/80 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>{t('CONTRACTUALIZATION.REAL_ESTATE.DETAILS_TOGGLE')}</span>
            <span className="text-gray-400 font-normal">
              ({t('CONTRACTUALIZATION.REAL_ESTATE.DETAILS_HINT')})
            </span>
          </button>
        ) : (
          <div className="flex flex-col gap-4 animate-fade-in">
            <button
              type="button"
              onClick={() => setShowDetails(false)}
              className="inline-flex items-center gap-1.5 self-start text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <Minus className="h-4 w-4" />
              <span>{t('CONTRACTUALIZATION.REAL_ESTATE.DETAILS_HIDE')}</span>
            </button>
            <BorrowerRealEstateDetailsSection />
          </div>
        )}
      </div>
    </Form>
  );
};
